import { useCallback } from 'react';
import { type DefaultError, useMutation } from '@tanstack/react-query';
import { type SupportedLocale } from '@zep-us/i18n';

import { logger } from '@zep/core-logger';

import { AI_HTTP_CLIENT } from '@/api/ai/client';
import { useLocale } from '@/hooks';
import { useBuildFormContext } from '@/views/build/form/schema';
import { useShowAiErrorAlert } from '@/views/build/hook';

import { useQuizFormField } from '../../hook/useQuizFormField';
import { useQuizControl } from '../../QuizControlProvider';
import { useSetAiGenerateMultipleStatus } from '../useAiGenerateMultipleStatus';

export const useAiSingleChoiceGeneration = (index: number) => {
  const [choice] = useQuizFormField({
    field: `multipleData.choiceList.${index}`,
  });

  const { setValue, getValues } = useBuildFormContext();
  const { id } = useQuizControl();
  const { addIndex, removeIndex } = useSetAiGenerateMultipleStatus();

  const [question] = useQuizFormField({
    field: 'question',
  });

  const { mutateAsync, isPending } = useAiGenerateSingleChoiceMutation();
  const { locale } = useLocale();
  const showAiErrorAlert = useShowAiErrorAlert();

  const disabled = isPending || !question;

  const generate = useCallback(async () => {
    const start = Date.now();
    logger.track('add_multiple_choice_ai_start', {
      location: 'single',
    });
    addIndex(index);
    try {
      const choiceText = await mutateAsync({
        question,
        isAnswer: choice.isAnswer,
        previousOutput: choice.text,
        locale,
      });
      if (!choiceText) return;

      const quizList = getValues(`quizList`);
      const targetQuizIndex = quizList.findIndex(quiz => quiz.id === id);
      if (targetQuizIndex === -1) {
        console.error(`target quiz not found: ${id}`);
        return;
      }

      setValue(`quizList.${targetQuizIndex}.multipleData.choiceList.${index}`, {
        ...choice,
        text: choiceText,
        richText: choiceText,
      });
    } catch (e) {
      console.warn('ai choice generation error', e);
      const duration = (Date.now() - start) / 1000;
      logger.track('add_multiple_choice_ai_error', {
        location: 'single',
        duration_seconds: duration,
      });
      showAiErrorAlert();
    } finally {
      removeIndex(index);
    }
  }, [
    mutateAsync,
    question,
    choice,
    setValue,
    locale,
    showAiErrorAlert,
    addIndex,
    removeIndex,
    index,
    id,
    getValues,
  ]);

  return {
    generate,
    disabled,
  };
};

export interface AiGenerateSingleChoiceQuery {
  subject?: string;
  schoolType?: string;
  grade?: string;
  question: string;
  isAnswer: boolean;
  previousOutput?: string;
  locale?: SupportedLocale;
}

export const useAiGenerateSingleChoiceMutation = () => {
  return useMutation<string, DefaultError, AiGenerateSingleChoiceQuery>({
    mutationFn: async query => {
      const res = await AI_HTTP_CLIENT.post(
        `/quiz-builder/generate/single-choice`,
        query,
      );
      return res.data;
    },
  });
};
